(function (global, factory) {
  'use strict';

  if (typeof define === 'function' && define.amd) {
    // If AMD is available, return to the AMD Loader
    // and to the global object for compatibility.
    define(
      'searchBar',
      [
        'loadCss',
        'searchBarComponent',
        'searchBarResultHandler',
        'currentLocationComponent',
        'searchBarHelper'
      ],
      (loadCss, SearchBarComponent, SearchBarResultHandler, CurrentLocationComponent, SearchBarHelper) => {
        global.SearchBar = factory(
          global,
          loadCss,
          SearchBarComponent,
          SearchBarResultHandler,
          CurrentLocationComponent,
          SearchBarHelper
        );

        return global.SearchBar;
      }
    );
  } else if (typeof exports === 'object') {
    // If CommonJS is available, export the module
    // and attach it to the global object.
    global.SearchBar = factory(
      global,
      global.loadCss || (global.NewHomeSource && global.NewHomeSource.loadCss),
      global.NewHomeSource && global.NewHomeSource.SearchBarComponent,
      global.NewHomeSource && global.NewHomeSource.SearchBarResultHandler,
      global.NewHomeSource && global.NewHomeSource.CurrentLocationComponent,
      global.SearchBarHelper
    );
    module.exports = global.SearchBar;
  } else {
    // Otherwise, attach to global scope.
    global.SearchBar = factory(
      global,
      global.loadCss || (global.NewHomeSource && global.NewHomeSource.loadCss),
      global.NewHomeSource && global.NewHomeSource.SearchBarComponent,
      global.NewHomeSource && global.NewHomeSource.SearchBarResultHandler,
      global.NewHomeSource && global.NewHomeSource.CurrentLocationComponent,
      global.SearchBarHelper
    );
  }
}(
  typeof window !== 'undefined' ? window : {},
  (global, loadCss, SearchBarComponent, SearchBarResultHandler, CurrentLocationComponent, SearchBarHelper) => {
    'use strict';

    /**
     * searchBar.js provides functionality for searching communities, locations, builders, and articles.
     * It depends on the following components:
     * - loadCss: A utility to dynamically load CSS files for mobile devices.
     * - searchBarComponent: A component that represents the search bar UI. It is reusable.
     * - If we need to use this component in other places, we can directly use this component.
     * - searchBarResultHandler: A component that handles search results. It is dedicated to the search bar.
     * - currentLocationComponent: A component that displays the current location. It is reusable.
     * - If we need to use this component in other places, we can directly use this component.
     */

    global.NewHomeSource = global.NewHomeSource || {};
    const previousComponent = global.NewHomeSource.SearchBar;

    const searchBarInstances = [];
    const searchBarResultHandlerInstances = [];
    const currentLocationComponentInstances = [];
    const searchBarInputs = [];

    /**
     * Checks if the current device is a mobile device.
     * @returns {boolean} True if the device is mobile, false otherwise.
     */
    function isMobileDevice() {
      const isMobileElement = document.querySelector('[data-is-mobile-view-port]');
      return !!isMobileElement;
    }

    /**
     * Checks if there is a search bar element outside the modal.
     * @returns {HTMLElement|null} The search bar element or null if not found.
     */
    function doesSearchBarHaveOutsideModalElement() {
      const element = document.querySelector('[data-is-mobile-search-bar-outside-modal]');
      return !!element;
    }

    const searchBarState = {
      searchText: null,
      searchBarContainer: null,
      isMobile: isMobileDevice()
    };

    /**
     * Updates the internal state of the search bar.
     * @param {Object} state - The new state to set.
     */
    function updateSearchBarState(state) {
      searchBarState.searchText = state && state.searchText ? state.searchText.trim() : null;
      searchBarState.searchBarContainer = state && state.searchBarContainer ? state.searchBarContainer : null;
      searchBarState.isMobile = isMobileDevice();
    }

    /**
     * Reads the last searched text from a cookie.
     * @returns {string|null} The last searched text or null if not found.
     */
    function readLastSearchCookie() {
      const cookieData = `; ${document.cookie}`;
      const parts = cookieData.split(`; ${'Personal_1'}=`);
      const lastSearchedText = parts.pop().split(';').shift();

      const searchText = lastSearchedText
        ? new URLSearchParams(lastSearchedText).get('searchtext') || null
        : null;

      return searchText;
    }

    // Keep track of the current AbortController for fetch requests
    let currentController = null;

    /**
     * Fetches search results from the server.
     * @param {string} searchText - The text to search for.
     * @returns {Promise<Object|null>} A promise that resolves to the search results or null on failure.
     *
     * This API call is intentionally kept within this file to maintain simplicity,
     * based on discussions with front-end experts.
     * If the logic becomes more complex in the future,
     * please refactor and move it to a dedicated service.
     */
    function fetchSearchResults(searchText) {
      if (!searchText.trim()) {
        return Promise.resolve(null);
      }

      // Cancel previous fetch if it exists
      if (currentController) {
        currentController.abort();
      }

      currentController = new AbortController();
      const signal = currentController.signal;

      return fetch('/getsearchbarresults?searchterm=' + encodeURIComponent(searchText), { signal })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null) // silently ignore all errors including AbortError
        .finally(() => {
          if (currentController === signal) {
            currentController = null;
          }
        });
    }

    /**
     * Checks if the provided text has any non-whitespace characters.
     * @param {string} text - The text to check.
     * @returns {boolean} True if there is text, false otherwise.
     */
    function hasSearchText(text) {
      return text && text.trim().length > 0;
    }

    /**
     * Handles the submit button click event.
     */
    function handleSubmitButtonClickEvent() {
      const searchText = readLastSearchCookie() || '';

      if (hasSearchText(searchText)) {
        fetchSearchResults(searchText).then((response) => {
          if (response && response.Results && response.Results.length > 0) {
            window.location.href = response.Results[0].LocationUrl;
          }
        });
      }
    }

    /**
     * Handles the Enter key event on the search input.
     * @param {KeyboardEvent} event - The keyboard event.
     */
    function handleEnterKey(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSubmitButtonClickEvent();
      }
    }

    /**
     * Destroys all instances of SearchBarComponent, SearchBarResultHandler, and CurrentLocationComponent.
     */
    function destroyInstances() {
      if (Array.isArray(searchBarInstances)) {
        while (searchBarInstances.length > 0) {
          const instance = searchBarInstances.pop();
          instance.destroyInstances();
        }
      }

      if (Array.isArray(searchBarResultHandlerInstances)) {
        while (searchBarResultHandlerInstances.length > 0) {
          const instance = searchBarResultHandlerInstances.pop();
          instance.destroyInstances();
        }
      }

      if (Array.isArray(currentLocationComponentInstances)) {
        while (currentLocationComponentInstances.length > 0) {
          const instance = currentLocationComponentInstances.pop();
          instance.destroyInstances();
        }
      }

      if (Array.isArray(searchBarInputs)) {
        while (searchBarInputs.length > 0) {
          const input = searchBarInputs.pop();
          input.removeEventListener('keydown', handleEnterKey);
        }
      }
    }

    /**
     * Gets the modal element for the search bar.
     * @returns {HTMLElement|null} The modal element or null if not found.
     */
    function getModalElement() {
      return document.querySelector('[data-search-bar-modal]');
    }

    /**
     * Show or hide the search results message container based on search text and device type.
     */
    function toggleSearchResultsMessageContainer(forceHide = false) {
      if (isMobileDevice()) {
        const modal = getModalElement();
        if (!modal) {
          return;
        }

        const container = modal.querySelector('[data-search-results-info]');
        if (container) {
          container.hidden = forceHide || (searchBarState.searchText || '').trim().length > 0;

          const searchKeywordContainer = container.querySelector('[data-search-keyword-container]');
          searchKeywordContainer.hidden = true;
        }
      }
    }

    /**
     * Show or hide a list of elements.
     * @param {HTMLElement[]} elements - Array of elements
     * @param {boolean} shouldShow - Whether to show (true) or hide (false)
     */
    function setElementsVisibility(elements, shouldShow) {
      elements.forEach((element) => {
        if (element) {
          const uiElement = element;
          uiElement.hidden = !shouldShow;
        }
      });
    }

    /**
     * Core logic to determine which bars to show/hide based on device type.
     * @param {HTMLElement[]} mobileBars
     * @param {HTMLElement[]} desktopBars
     * @param {boolean} isMobile - True if device is mobile
     */
    function toggleBars(mobileBars, desktopBars, isMobile) {
      if (isMobile) {
        setElementsVisibility(desktopBars, false);
        setElementsVisibility(mobileBars, true);
      } else {
        setElementsVisibility(mobileBars, false);
        setElementsVisibility(desktopBars, true);
      }
    }

    /**
     * Toggles the visibility of search bars based on the device type.
     */
    function toggleSearchBarsForDevice() {
      const mobileSearchBars = document.querySelectorAll('[data-search-bar-container-mobile]');
      const desktopSearchBars = document.querySelectorAll('[data-search-bar-container-desktop]');
      toggleBars(mobileSearchBars, desktopSearchBars, isMobileDevice());
    }

    /**
     * Loads the CSS for the modal.
     */
    function loadModalCss() {
      const cssBundle = document.querySelector('#searchPanelExpanded');
      if (cssBundle) {
        loadCss(cssBundle.value);
      }
    }

    /**
     * Debounces a function that returns a promise.
     * @param {Function} func - The function to debounce.
     * @param {number} wait - The debounce delay in milliseconds.
     */
    function debouncePromise(func, wait) {
      let timeout;
      let lastCall = null;

      return (...args) => {
        clearTimeout(timeout);

        return new Promise((resolve, reject) => {
          lastCall = { args, resolve, reject };
          timeout = setTimeout(() => {
            timeout = null;
            const current = lastCall;
            lastCall = null;
            func(...current.args).then(current.resolve).catch(current.reject);
          }, wait);
        });
      };
    }

    let searchResults = [];

    /**
     * Displays the search results using SearchBarResultHandler.
     */
    function displaySearchResults() {
      const config = {
        searchText: searchBarState.searchText,
        searchBarContainer: searchBarState.searchBarContainer,
        isMobile: searchBarState.isMobile,
        searchResults
      };

      const instance = new SearchBarResultHandler(config);
      searchBarResultHandlerInstances.push(instance);
    }

    /**
     * Fetches search results and renders them using SearchBarResultHandler.
     * @param {string} searchText - The text to search for.
     */
    function fetchResultsAndRenderSearchResult(searchText) {
      const debouncedFetchSearchResults = debouncePromise(fetchSearchResults, 300);
      debouncedFetchSearchResults(searchText).then((response) => {
        if (response && response.Results && response.Results.length > 0) {
          searchResults = response.Results;
        }
      }).finally(() => {
        displaySearchResults();
      });
    }

    /**
     * Binds the Enter key event to the search input element
     * and returns a cleanup function to remove it later.
     * @param {HTMLElement} inputElement - The search input element.
     * @returns {Function|null} Cleanup function to remove the event listener.
     */
    function bindSearchInputEnterKeyEvent(inputElement) {
      if (!inputElement) {
        return;
      }

      inputElement.addEventListener('keydown', handleEnterKey);
      searchBarInputs.push(inputElement);
    }

    /**
     * Subscribes to the open status changes of the CurrentLocationComponent instance.
     * @param {CurrentLocationComponent} instance - The CurrentLocationComponent instance.
     */
    function subscribeToCurrentLocationOpenStatus(instance) {
      instance.subscribeOpenStatus((isOpen) => {
        toggleSearchResultsMessageContainer(isOpen);
      });
    }

    /**
     * Displays the current location component.
     */
    function displayCurrentLocation() {
      const currentLocationComponent = new CurrentLocationComponent({
        searchBarState
      });

      currentLocationComponentInstances.push(currentLocationComponent);

      if (isMobileDevice()) {
        subscribeToCurrentLocationOpenStatus(currentLocationComponent);
      }
    }

    /**
     * Displays the last searched text in the mobile home.
     * It has not search functionality, just display purpose.
     * @param {string} searchText - The text to display.
     */
    function displayLastSearchTextHomeMobile(searchText) {
      if (isMobileDevice()) {
        const inputReadonlyElement = document.querySelector('[data-home-mobile-search-input]');
        if (inputReadonlyElement) {
          inputReadonlyElement.value = searchText;
        }
      }
    }

    /**
     * Handles the search bar event.
     * @param {string} searchText - The text entered in the search bar.
     * @param {string} searchBarContainer - The container element for the search bar.
     */
    function handleSearchBarInputUpdateEvent(searchText, searchBarContainer) {
      updateSearchBarState({
        searchText,
        searchBarContainer
      });

      displayCurrentLocation();
      toggleSearchResultsMessageContainer();

      if (hasSearchText(searchText)) {
        fetchResultsAndRenderSearchResult(searchText);
      } else {
        searchResults = [];
        displaySearchResults();
      }
    }

    /**
     * Handles the current location when the search input is focused.
     * @param {string} container - The container element for the search bar.
     */
    function handleCurrentLocation(container, value) {
      updateSearchBarState({
        searchText: value,
        searchBarContainer: container
      });

      displayCurrentLocation();
    }

    /**
     * Subscribe to events from the SearchBar component.
     * @param {SearchBarComponent} instance - The SearchBar component instance.
     */
    function subscribeToSearchBarComponentEvents(instance) {
      instance.getInputChangeObservable().subscribe(({ container, value }) => {
        handleSearchBarInputUpdateEvent(value, container);
        displayLastSearchTextHomeMobile(value);
      });

      instance.getSubmitButtonClickObservable().subscribe(() => {
        handleSubmitButtonClickEvent();
      });

      instance.getInputFocusObservable().subscribe(({ container, value }) => {
        handleCurrentLocation(container, value);
      });
    }

    /**
     * Updates the search results message container visibility.
     */
    function updateSearchResultsMessageContainer() {
      toggleSearchResultsMessageContainer();
    }

    /**
     * Creates a configuration object for UI visibility management.
     * @returns {Object} The configuration object.
     */
    function createUIVisibilityConfig() {
      const homeSearchBar = document.querySelector('[data-home-search-bar]');
      const detailsContainer = document.querySelector('[data-details-container]');
      const header = document.querySelector('[data-main-header]');
      const sideMenu = document.querySelector('[data-menu-side]');
      const modal = getModalElement();

      return {
        modal,
        homeSearchBar,
        detailsContainer,
        header,
        sideMenu,
        loadModalCss
      };
    }

    /**
     * Sets the visibility of the search bar modal.
     * @param {boolean} shouldShow - Whether to show the modal.
     */
    function setModalVisibility(shouldShow) {
      const modal = getModalElement();
      if (modal) {
        modal.hidden = !shouldShow;
      }
    }

    /**
     * Toggles the mobile search bar modal.
     * @param {boolean|null} forceOpen - If true, forces the modal to open;
     * if false, forces it to close; if null, toggles the current state.
     */
    function toggleMobileSearchBarModal(forceOpen = null) {
      const modal = getModalElement();
      if (!modal) {
        return;
      }

      updateSearchResultsMessageContainer();

      const isModalOpen = !modal.hidden;
      const shouldOpenModal = forceOpen === null ? !isModalOpen : forceOpen;

      const config = createUIVisibilityConfig();
      SearchBarHelper.updateLayoutVisibility(shouldOpenModal, config);
      setModalVisibility(shouldOpenModal);
    }

    let mobileSearchEventsBound = false;

    /**
     * Binds events for mobile search functionality.
     */
    function bindMobileEvents() {
      if (!mobileSearchEventsBound) {
        const mobileMenuButton = document.querySelector('[data-open-search-bar-modal]');
        const closeButton = document.querySelector('[data-close-search-bar-modal]');

        if (mobileMenuButton) {
          mobileMenuButton.addEventListener('click', () => toggleMobileSearchBarModal(true));
        }

        if (closeButton) {
          closeButton.addEventListener('click', () => toggleMobileSearchBarModal(false));
        }

        // Mark as bound only after attempting registrations (prevents duplicate listeners)
        mobileSearchEventsBound = true;
      }
    }

    /**
     * Finds the mobile search bar element outside the modal.
     * @returns {HTMLElement|null} The mobile search bar element or null if not found.
     */
    function findMobileSearchBarOutsideModal() {
      if (doesSearchBarHaveOutsideModalElement() && isMobileDevice()) {
        return document.querySelector('[data-mobile-search-bar-outside-modal]');
      }
      return null;
    }

    /**
     * Shows a list of elements.
     * @param {HTMLElement[]} elements - Array of elements to show.
     */
    function showElements(elements) {
      if (elements && elements.length) {
        setElementsVisibility(elements, true);
      }
    }

    /**
     * Gets the search bar containers, including the mobile search bar outside the modal if it exists.
     * @param {HTMLElement[]} containers - Array of existing search bar containers.
     * @returns {HTMLElement[]} Updated array of search bar containers.
     */
    function getSearchBarContainers(containers) {
      const mobileSearchBar = findMobileSearchBarOutsideModal();
      if (!mobileSearchBar) {
        return containers;
      }

      showElements([mobileSearchBar]);
      containers.push(mobileSearchBar);
      loadModalCss();
      return containers;
    }

    /**
     * Initializes the search bar components.
     * @param {boolean} cleanupUnusedInstances - Whether to clean up unused instances.
     */
    function initializeSearchBarComponents(cleanupUnusedInstances = true) {
      if (cleanupUnusedInstances) {
        destroyInstances();
      }

      let searchBarContainerElements = document.querySelectorAll('[data-search-bar-container-desktop]');
      toggleSearchBarsForDevice();

      if (isMobileDevice()) {
        bindMobileEvents();
        searchBarContainerElements = getModalElement().querySelectorAll('[data-search-bar-container-mobile]');
        displayLastSearchTextHomeMobile(readLastSearchCookie() || '');
      }

      const searchBarContainers = getSearchBarContainers(Array.from(searchBarContainerElements));
      const lastSearchText = readLastSearchCookie();

      searchBarContainers.forEach((searchBarContainer) => {
        const searchBarSubmitButton = searchBarContainer.querySelector('[data-search-bar-submit-button]');
        const lastSearchFlag = searchBarContainer.querySelector('[data-suppress-last-search]');
        const suppressLastSearch = !!(lastSearchFlag && lastSearchFlag.value === 'true');

        const config = {
          searchBarContainer,
          searchInput: searchBarContainer.querySelector('[data-search-input]'),
          resetButton: searchBarContainer.querySelector('[data-search-input-reset-button]'),
          submitButton: searchBarSubmitButton || document.querySelector('[data-search-bar-submit-button]'),
          searchBarInputHighlightContainer: searchBarContainer.querySelector('[data-search-bar-input-container]'),
          lastSearchedText: suppressLastSearch ? '' : lastSearchText
        };

        updateSearchBarState({
          searchText: config.lastSearchedText,
          searchBarContainer: config.searchBarContainer
        });

        const instance = new SearchBarComponent(config);
        searchBarInstances.push(instance);
        subscribeToSearchBarComponentEvents(instance);
        bindSearchInputEnterKeyEvent(config.searchInput);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initializeSearchBarComponents);
    } else {
      initializeSearchBarComponents();
    }

    /**
     * API for the SearchBar component
     */
    const api = {
      destroyInstances,
      noConflict() {
        global.NewHomeSource.SearchBar = previousComponent;
        return api;
      }
    };

    global.NewHomeSource.SearchBar = api;
    return api;
  }
));
